import SwaggerParser from "@apidevtools/swagger-parser";
import type { OpenAPIV3 } from "openapi-types";
import { ParsedRoute, ParsedSpec, HttpMethod, SpecInput } from "./types";

const SUPPORTED_METHODS: HttpMethod[] = ["get", "post", "put", "delete", "patch"];

export async function parseSpec(spec: SpecInput): Promise<ParsedSpec> {
  let document: OpenAPIV3.Document;

  try {
    if (typeof spec === "string") {
      // Treat as path or URL
      const api = (await SwaggerParser.dereference(spec)) as OpenAPIV3.Document;
      document = api;
    } else {
      // Inline document
      const api = (await SwaggerParser.dereference(spec as any)) as OpenAPIV3.Document;
      document = api;
    }
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Unknown error while parsing OpenAPI spec.";
    throw new Error(`Failed to parse OpenAPI specification: ${message}`);
  }

  if (!document.paths || typeof document.paths !== "object") {
    throw new Error("Invalid OpenAPI specification: missing or invalid 'paths' field.");
  }

  const routes: ParsedRoute[] = [];

  for (const [path, item] of Object.entries(document.paths)) {
    if (!item) continue;

    for (const method of SUPPORTED_METHODS) {
      const operation = (item as any)[method] as OpenAPIV3.OperationObject | undefined;
      if (!operation) continue;

      const { schema: responseSchema, statusCode } = extractResponse(operation);

      routes.push({
        method,
        path,
        operationId: operation.operationId,
        summary: operation.summary,
        description: operation.description,
        responseSchema,
        statusCode
      });
    }
  }

  if (routes.length === 0) {
    throw new Error("No supported routes (GET, POST, PUT, DELETE, PATCH) found in spec.");
  }

  return { routes };
}

function extractResponse(
  operation: OpenAPIV3.OperationObject
): { schema?: OpenAPIV3.SchemaObject; statusCode?: number } {
  if (!operation.responses || typeof operation.responses !== "object") {
    return {};
  }

  // Prefer 200, then other 2xx, then default, then any
  const responsesEntries = Object.entries(operation.responses);

  const sorted = responsesEntries.sort(([a], [b]) => {
    if (a === "200") return -1;
    if (b === "200") return 1;
    const aNum = parseInt(a, 10);
    const bNum = parseInt(b, 10);
    const aIs2xx = aNum >= 200 && aNum < 300;
    const bIs2xx = bNum >= 200 && bNum < 300;
    if (aIs2xx && !bIs2xx) return -1;
    if (!aIs2xx && bIs2xx) return 1;
    if (a === "default") return -1;
    if (b === "default") return 1;
    return 0;
  });

  for (const [code, resp] of sorted) {
    const response = resp as OpenAPIV3.ResponseObject;
    const content = response.content;
    if (!content) continue;

    const jsonLike =
      content["application/json"] ||
      content["application/*+json"] ||
      Object.values(content)[0];

    if (!jsonLike || !jsonLike.schema) continue;

    const schema = jsonLike.schema as OpenAPIV3.SchemaObject;
    const statusCode = code === "default" ? undefined : parseInt(code, 10) || undefined;
    return { schema, statusCode };
  }

  return {};
}
